import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import PolicyLayout from './PolicyLayout';

const DataRetention = () => {
  const retentionPeriods = [
    {
      dataType: 'Account Information',
      description: 'Name, email, practice details',
      retention: 'Duration of account + 3 years',
      basis: 'Contract performance, legal obligations'
    },
    {
      dataType: 'Claims Data',
      description: 'Patient claims, denial information',
      retention: '7 years from creation',
      basis: 'HIPAA requirements, legal compliance'
    },
    {
      dataType: 'Appeal Documents',
      description: 'Generated appeals, supporting documents',
      retention: '7 years from creation',
      basis: 'HIPAA requirements, audit trail'
    },
    {
      dataType: 'Audit Logs',
      description: 'System access, data modifications',
      retention: '6 years',
      basis: 'HIPAA Security Rule, compliance'
    },
    {
      dataType: 'Billing Records',
      description: 'Payment history, invoices',
      retention: '7 years',
      basis: 'Tax and financial regulations'
    },
    {
      dataType: 'Support Communications',
      description: 'Tickets, emails, chat logs',
      retention: '3 years',
      basis: 'Service improvement, dispute resolution'
    },
    {
      dataType: 'Analytics Data',
      description: 'Usage statistics, performance metrics',
      retention: '2 years',
      basis: 'Service improvement (anonymized)'
    },
    {
      dataType: 'Marketing Preferences',
      description: 'Communication opt-ins/opt-outs',
      retention: 'Until changed or account deleted',
      basis: 'Consent management'
    }
  ];

  return (
    <PolicyLayout title="Data Usage & Retention Policy" lastUpdated="January 20, 2026">
      <p>
        This policy describes how AppealBot collects, uses, and retains your data. We are
        committed to transparent data practices and retaining data only as long as necessary
        for legitimate business purposes and legal compliance.
      </p>

      <h2>1. Data Collection Principles</h2>
      <p>We adhere to the following principles when collecting data:</p>
      <ul>
        <li><strong>Minimization:</strong> We collect only the data necessary to provide our services</li>
        <li><strong>Purpose Limitation:</strong> Data is used only for specified, legitimate purposes</li>
        <li><strong>Accuracy:</strong> We take steps to ensure data accuracy and provide correction mechanisms</li>
        <li><strong>Security:</strong> Appropriate safeguards protect data throughout its lifecycle</li>
        <li><strong>Transparency:</strong> We clearly communicate our data practices</li>
      </ul>

      <h2>2. How We Use Your Data</h2>

      <h3>2.1 Service Delivery</h3>
      <ul>
        <li>Processing insurance claims and generating appeals</li>
        <li>Managing your account and subscription</li>
        <li>Providing customer support</li>
        <li>Sending transactional notifications</li>
      </ul>

      <h3>2.2 Service Improvement</h3>
      <ul>
        <li>Analyzing usage patterns to enhance features</li>
        <li>Training and improving our AI models (using de-identified data only)</li>
        <li>Conducting research and development</li>
        <li>Troubleshooting and fixing issues</li>
      </ul>

      <h3>2.3 Compliance & Security</h3>
      <ul>
        <li>Maintaining audit trails as required by HIPAA</li>
        <li>Detecting and preventing fraud or abuse</li>
        <li>Meeting legal and regulatory obligations</li>
        <li>Responding to legal requests</li>
      </ul>

      <h2>3. Data Retention Periods</h2>
      <p>
        The following table outlines our standard retention periods. Retention may be extended
        when required by law, pending litigation, or regulatory investigation.
      </p>

      <TableContainer component={Paper} sx={{ my: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Data Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Retention Period</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Legal Basis</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {retentionPeriods.map((row, index) => (
              <TableRow key={index}>
                <TableCell sx={{ fontWeight: 'medium' }}>{row.dataType}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.retention}</TableCell>
                <TableCell>{row.basis}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <h2>4. Healthcare Data (PHI)</h2>
      <p>
        Protected Health Information is subject to specific retention requirements under HIPAA:
      </p>
      <ul>
        <li>PHI is retained for a minimum of 6 years from creation or last effective date</li>
        <li>Some states require longer retention periods (we comply with the longest applicable period)</li>
        <li>PHI related to minors may be retained until the patient reaches age of majority plus applicable retention period</li>
        <li>Audit logs related to PHI access are retained for 6 years</li>
      </ul>

      <h2>5. Data Deletion</h2>

      <h3>5.1 Account Deletion</h3>
      <p>When you request account deletion:</p>
      <ul>
        <li>Account access is immediately revoked</li>
        <li>Personal identifiers are removed or anonymized within 30 days</li>
        <li>PHI is retained per HIPAA requirements, then securely deleted</li>
        <li>Backups containing your data are overwritten within 90 days</li>
      </ul>

      <h3>5.2 Data Deletion Requests</h3>
      <p>
        You may request deletion of specific data. Note that we may be required to retain
        certain data for legal compliance. We will inform you if we cannot fully honor your
        request and explain the reasons.
      </p>

      <h3>5.3 Secure Deletion</h3>
      <p>When data is deleted, we use secure deletion methods:</p>
      <ul>
        <li>Cryptographic erasure for encrypted data</li>
        <li>Secure overwriting for unencrypted data</li>
        <li>Physical destruction for decommissioned hardware</li>
      </ul>

      <h2>6. Data Portability</h2>
      <p>You have the right to export your data:</p>
      <ul>
        <li>Request a copy of your data in a machine-readable format</li>
        <li>Export includes account information, claims data, and appeal documents</li>
        <li>Requests are fulfilled within 30 days</li>
        <li>Contact support@appealbot.com to request an export</li>
      </ul>

      <h2>7. AI and Machine Learning</h2>
      <p>Regarding the use of your data in AI systems:</p>
      <ul>
        <li>We use de-identified, aggregated data to improve our AI models</li>
        <li>Individual PHI is not used for training without explicit consent</li>
        <li>AI improvements benefit all users through better appeal generation</li>
        <li>You may opt out of contributing to AI improvement</li>
      </ul>

      <h2>8. Third-Party Data Sharing</h2>
      <p>
        Data shared with third parties is subject to data processing agreements that include:
      </p>
      <ul>
        <li>Restrictions on data use and retention</li>
        <li>Security requirements</li>
        <li>Deletion obligations upon contract termination</li>
        <li>Audit rights</li>
      </ul>

      <h2>9. Changes to Retention Periods</h2>
      <p>
        We may update retention periods to comply with legal changes or business needs.
        Material changes will be communicated through our platform and may be reflected
        in updated privacy notices.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        For questions about data retention or to make a data request:
      </p>
      <ul>
        <li>Email: privacy@appealbot.com</li>
        <li>Subject line: "Data Request" or "Retention Inquiry"</li>
      </ul>
    </PolicyLayout>
  );
};

export default DataRetention;
